import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { Card, Btn, Badge, EmptyState, AInput, ATextarea, ASelect } from "../AdminUI";
import PhotoManager from "@/components/admin/PhotoManager";
import { GALLERY_CATEGORIES } from "@/lib/config";
import { Upload, Trash2, Image as ImageIcon, Plus, Loader2, Play } from "lucide-react";

const CATS = GALLERY_CATEGORIES.slice(1);

export default function Media() {
  const [tab, setTab] = useState("projects");
  const [media, setMedia] = useState([]);
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [edit, setEdit] = useState(null);
  const fileRef = useRef();

  const loadMedia = () => api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
  const loadProjects = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { loadMedia(); loadProjects(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try { await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(err.response?.data?.detail || `Failed: ${file.name}`); }
    }
    setUploading(false); toast.success("Upload complete"); loadMedia();
  };
  const delMedia = async (id) => { if (!window.confirm("Delete this file?")) return; await api.delete(`/admin/media/${id}`); loadMedia(); };

  const blank = { title: "", category: "Driveways", property_type: "residential", city: "", description: "", before_image: "", after_image: "", before_images: [], after_images: [], type: "before_after", featured: false, status: "draft" };
  const openEditor = (p) => {
    const bi = p.before_images?.length ? p.before_images : (p.before_image ? [{ id: p.before_image, caption: "" }] : []);
    const ai = p.after_images?.length ? p.after_images : (p.after_image ? [{ id: p.after_image, caption: "" }] : []);
    setEdit({ ...blank, ...p, before_images: bi, after_images: ai });
  };
  const saveP = async () => {
    const payload = { ...edit, before_image: edit.before_images?.[0]?.id || "", after_image: edit.after_images?.[0]?.id || "" };
    try {
      if (edit.id) await api.patch(`/admin/projects/${edit.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved"); setEdit(null); loadProjects();
    } catch { toast.error("Failed"); }
  };
  const delP = async (id) => { if (!window.confirm("Delete project?")) return; await api.delete(`/admin/projects/${id}`); loadProjects(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Media Center</h1>
        <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setTab("projects")} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${tab === "projects" ? "bg-white shadow text-[var(--navy)]" : "text-gray-500"}`} data-testid="tab-projects">Gallery Projects</button>
          <button onClick={() => setTab("library")} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${tab === "library" ? "bg-white shadow text-[var(--navy)]" : "text-gray-500"}`} data-testid="tab-library">Media Library</button>
        </div>
      </div>

      {/* Upload always available */}
      <Card>
        <label className="flex items-center justify-center gap-3 h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[var(--action)] text-gray-500">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-sm">{uploading ? "Uploading…" : "Upload photos or videos (drag & drop or click). Public gallery storage."}</span>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={upload} data-testid="media-upload" />
        </label>
      </Card>

      {tab === "library" && (
        media.length === 0 ? <Card><EmptyState icon={ImageIcon} title="No media yet" hint="Upload photos and videos to build your gallery." /></Card> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {media.map((m) => (
              <div key={m.id} className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden">
                {m.is_video ? <div className="h-32 bg-black flex items-center justify-center"><Play className="w-8 h-8 text-white" /></div>
                  : <img src={resolveImage(m.id)} alt={m.original_filename} className="h-32 w-full object-cover" />}
                <div className="p-2"><p className="text-[11px] text-gray-500 truncate">{m.original_filename}</p>
                    <button onClick={() => delMedia(m.id)} className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "projects" && (
        <>
          <div className="flex justify-end"><Btn onClick={() => { setEdit(blank); setTab("projects"); }} data-testid="add-project"><Plus className="w-4 h-4" /> New Project</Btn></div>

          {edit && (
            <Card title={edit.id ? "Edit Project" : "New Project"}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AInput label="Title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
                <ASelect label="Category" value={edit.category} options={CATS} onChange={(e) => setEdit({ ...edit, category: e.target.value })} />
                <ASelect label="Type" value={edit.property_type} options={["residential", "commercial"]} onChange={(e) => setEdit({ ...edit, property_type: e.target.value })} />
                <AInput label="City (optional)" value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} />
                <ASelect label="Media Type" value={edit.type} options={[{value:"before_after",label:"Before & After"},{value:"photo",label:"Single Photo"},{value:"video",label:"Video"}]} onChange={(e) => setEdit({ ...edit, type: e.target.value })} />
                <ASelect label="Status" value={edit.status} options={["published", "draft"]} onChange={(e) => setEdit({ ...edit, status: e.target.value })} />
                <div className="sm:col-span-2"><ATextarea label="Description" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} className="w-4 h-4 accent-[var(--action)]" /> Featured project</label>
              <div className="mt-4 space-y-5">
                <PhotoManager label="Before Photos" value={edit.before_images || []} onChange={(arr) => setEdit({ ...edit, before_images: arr })} />
                <PhotoManager label="After Photos" value={edit.after_images || []} onChange={(arr) => setEdit({ ...edit, after_images: arr })} />
              </div>
              <div className="flex gap-2 mt-4"><Btn onClick={saveP} data-testid="save-project">Save Project</Btn><Btn variant="ghost" onClick={() => setEdit(null)}>Cancel</Btn></div>
            </Card>
          )}

          {projects.length === 0 ? <Card><EmptyState icon={ImageIcon} title="No projects yet" hint="Create before-and-after projects to showcase your work." /></Card> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <Card key={p.id}>
                  <div className="flex gap-1 mb-2">
                    {p.before_image && <img src={resolveImage(p.before_image)} alt="" className="h-24 w-1/2 object-cover rounded" />}
                    {p.after_image && <img src={resolveImage(p.after_image)} alt="" className="h-24 w-1/2 object-cover rounded" />}
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[var(--navy)]">{p.title}</p>
                    <div className="flex gap-1">{p.featured && <Badge color="red">Featured</Badge>}<Badge color={p.status === "published" ? "green" : "amber"}>{p.status}</Badge></div>
                  </div>
                  <p className="text-xs text-gray-400">{p.property_type} · {p.category}{p.city && ` · ${p.city}`}</p>
                  <div className="flex gap-2 mt-3"><Btn variant="ghost" onClick={() => openEditor(p)} className="flex-1">Edit</Btn><Btn variant="danger" onClick={() => delP(p.id)}><Trash2 className="w-4 h-4" /></Btn></div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
